import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS, DetailsCard, PSA, ROB } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES, CAR_TYPE } from "models";
import "modules/CarDetails/components/Details/Details.scss";
import { useCarLocation } from "modules/CarDetails/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { SectionMenu } from "components";
import type { Car } from "models";

type DetailsProps = {
  sectionMenu: SectionMenu;
  car: Car;
};

const getVehicleTypeIconClass = (vehicleType: CAR_TYPE) => {
  switch (vehicleType) {
    case CAR_TYPE.Truck:
      return "truck";
    case CAR_TYPE.Motorcycle:
      return "motorcycle";
    case CAR_TYPE.Van:
      return "van shuttle";
    case CAR_TYPE.LightTruck:
      return "truck pickup";
    case CAR_TYPE.Unknown:
      return "circle question";
    default:
      return "car side";
  }
};

export const Details = ({ car, sectionMenu }: DetailsProps) => {
  const { t } = useTranslation();

  const carLocation = useCarLocation(car);

  return (
    <DetailsCard
      icon={sectionMenu.icon}
      title={sectionMenu.title}
      showExpandArrow={false}
      sectionMenuId={sectionMenu.id}
      rightContent={
        <div className="full end">
          <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.ServiceBoxCarWidget}>
            <PSA id={car.id} type="car" location={carLocation} />
          </Can>
          <Can I="list" the="robnet-contract">
            <ROB car_id={car.id} dealer_location_id={car.dealer_location_id} />
          </Can>
        </div>
      }
    >
      <div className="Details">
        {car.profile_picture && (
          <div className="Details-img">
            <img
              src={car.profile_picture}
              className="car-image"
              onError={e => {
                if (e.currentTarget.parentElement) e.currentTarget.parentElement.style.display = "none";
              }}
            />
          </div>
        )}
        <div className="Details-container">
          <div className="group">
            <div>
              <p className="title">{t("v8_license_plate", "License Plate")}</p>
              <p>{car.reg_nr}</p>
            </div>
            <div>
              <p className="title">{t("v8_brand", "Brand")}</p>
              <p className="title-data">{car.make}</p>
            </div>
          </div>

          <div className="group">
            <div>
              <p className="title">{t("v8_model", "Model")}</p>
              <div className="model-with-icon">
                <i className={`${getVehicleTypeIconClass(car.vehicle_type)} icon vehicle-type-icon`} />
                <Popup content={car.model} trigger={<p>{car.model}</p>} />
              </div>
            </div>
            <div>
              <p className="title">{t("v8_engine_number", "Engine Number")}</p>
              <Popup content={car.engine_number} trigger={<p className="data">{car.engine_number || "NA"}</p>} />
            </div>
          </div>

          {carLocation?.apk_visible && (
            <div className="group">
              <div>
                <p className="title">{t("v8_apk", "APK")}</p>
                <p className="title-data">{formatDate(car.apk_date_dms, DATE_FORMATS_FNS.dateMonthYear)}</p>
              </div>
            </div>
          )}

          <div className="group">
            <div>
              <p className="title">{t("v8_vin_chasis_number", "Vin/Chasis number")}</p>
              <Popup content={car.vin_nr} trigger={<p>{car.vin_nr || "NA"}</p>} />
            </div>
            <div>
              <p className="title">{t("v8_dms_registration", "DMS registration")}</p>
              <p className="title-data">{formatDate(car.dms_registration, DATE_FORMATS_FNS.dateMonthYear)}</p>
            </div>
          </div>

          <div className="group">
            <div>
              <p className="title">{t("v8_warranty_start", "Warranty start")}</p>
              <p className="title-data">{formatDate(car.warranty_date_start, DATE_FORMATS_FNS.dateMonthYear)}</p>
            </div>
            <div>
              <p className="title">{t("v8_warranty_end", "Warranty end")}</p>
              <p className="title-data">{formatDate(car.warranty_date_end, DATE_FORMATS_FNS.dateMonthYear)}</p>
            </div>
          </div>

          <div className="group">
            <div>
              <p className="title">{t("v8_extended_warranties", "Extended warranties")}</p>
              <p className="title-data">{formatDate(car.warranty_extended, DATE_FORMATS_FNS.dateMonthYear)}</p>
            </div>
            <div>
              <p className="title">{t("v8_warranty_type", "Warranty Type")}</p>
              <p className="title-data">{car.warranty_type}</p>
            </div>
          </div>

          <div className="group">
            <div>
              <p className="title">{t("v8_extended_warranty_type", "Extended Warranty Type")}</p>
              <p className="title-data">{car.warranty_extended_type}</p>
            </div>
          </div>
        </div>
      </div>
    </DetailsCard>
  );
};
